import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAccess } from "@/lib/access";
import { useAuth } from "@/lib/auth";
import { formatIDR, PROJECT_STATUS, deriveProjectStatus } from "@/lib/constants";
// 2026-06-20 (user request): badge Status OPERASIONAL (pekerjaan/penagihan/retensi).
import { OpStatusBadge } from "@/lib/operationalStatus";
import { resolveIdentity } from "@/lib/projectIdentity";
import EditProjectDialog from "@/components/EditProjectDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import RingkasanTab from "@/components/monitoring/RingkasanTab";
import ProfitStatusBadge from "@/components/cost-control/ProfitStatusBadge";
import ProfitDrilldownDialog from "@/components/cost-control/ProfitDrilldownDialog";
import DataProyekTab from "@/components/monitoring/DataProyekTab";
import ProgressLapanganTab from "@/components/monitoring/ProgressLapanganTab";
import KeuanganTab from "@/components/monitoring/KeuanganTab";
import CashFlowTab from "@/components/monitoring/CashFlowTab";
import WorkingOrderTab from "@/components/monitoring/WorkingOrderTab";
import RiwayatAktivitasTab from "@/components/monitoring/RiwayatAktivitasTab";

/**
 * MonitoringProjectPage — Dashboard Monitoring per proyek.
 * 2026-06-15: Rework Project Management sesuai spesifikasi user.
 * 2026-09-25 (audit placeholder): seluruh 7 tab sudah fungsional — komentar
 * "Fase 1 skeleton" lama dihapus supaya tidak menyesatkan.
 */
const TABS = [
  { key: "ringkasan", label: "Ringkasan" },
  { key: "progress-lapangan", label: "Progress Pekerjaan" },
  { key: "keuangan", label: "Keuangan" },
  { key: "cash-flow", label: "Cash Flow" },
  { key: "working-order", label: "Working Order" },
  { key: "data-proyek", label: "Data Proyek" },
  { key: "riwayat-aktivitas", label: "Riwayat Aktivitas" },
];

export default function MonitoringProjectPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const [sp, setSp] = useSearchParams();
  const activeTab = TABS.some((t) => t.key === sp.get("tab")) ? sp.get("tab") : (sp.get("focus_invoice") ? "keuangan" : "ringkasan");
  const [proj, setProj] = useState(null);
  const [mon, setMon] = useState(null);
  const [err, setErr] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  // 2026-06-20 (user request): ringkasan Cost Control untuk widget Profit.
  const [ccSum, setCcSum] = useState(null);
  const [ccDrillOpen, setCcDrillOpen] = useState(false);
  // Daftar lengkap proyek — untuk resolusi identitas induk (single source).
  const [allProjects, setAllProjects] = useState([]);

  const load = async () => {
    setErr(null);
    try {
      const [all, m, cc] = await Promise.all([
        api.get(`/active-projects`).then((r) => r.data || []),
        api.get(`/active-projects/${pid}/monitoring`).then((r) => r.data).catch(() => null),
        api.get(`/active-projects/${pid}/cost-control/summary`).then((r) => r.data).catch(() => null),
      ]);
      const p = all.find((x) => x.id === pid);
      if (!p) { setErr("Project tidak ditemukan atau Anda tidak memiliki akses."); return; }
      setAllProjects(all);
      setProj(p);
      setMon(m);
      setCcSum(cc);
    } catch (e) { setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat data project."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const setTab = (k) => setSp({ tab: k }, { replace: true });

  if (err) return (
    <div className="p-6 max-w-2xl" data-testid="mp-error">
      <p className="overline text-slate-500">Monitoring Project</p>
      <h1 className="font-display text-2xl text-[#0B1D3A] mt-1">{err}</h1>
      <button type="button" data-testid="mp-back" onClick={() => nav("/projects")}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold">
        <ArrowLeft size={13} /> Kembali ke Project
      </button>
    </div>
  );
  if (!proj) return <div className="p-8 text-sm text-slate-500" data-testid="mp-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat data project…</div>;

  const status = PROJECT_STATUS[deriveProjectStatus(proj)] || PROJECT_STATUS.active;
  // 2026-06-20 (user request): identitas (nomor kontrak/PO, nilai kontrak,
  // tanggal mulai/selesai, client, PIC) bersumber dari data INDUK proyek.
  // Data historis anak tidak ditimpa — konflik ditampilkan sebagai peringatan.
  const identity = resolveIdentity(proj, allProjects);
  const idf = identity.fields;
  const contractVal = Number(idf.contract_value ?? proj.contract_value ?? 0);
  const isWoProject = proj.project_type === "working_order";
  const isMaintenanceProject = !!proj.is_outlet && !isWoProject;
  const woRows = mon?.wo_list || [];
  // 2026-06-20 (user request): definisi seragam — WO dibatalkan keluar dari
  // denominator progress; selesai = tahap Selesai Fisik/Invoice/Closed.
  const woCancelled = Number(mon?.summary?.wo_cancelled ?? woRows.filter((w) => w.status === "cancelled" || w.status === "cancelled_by_owner").length);
  const woDone = Number(mon?.summary?.wo_done ?? woRows.filter((w) => ["selesai_fisik", "invoice", "closed"].includes(w.stage) || w.status === "completed").length);
  const woTotal = woRows.length || (Number(mon?.summary?.wo_total || 0) || (woDone + Number(mon?.summary?.wo_active || 0) + woCancelled));
  const woEff = Math.max(0, woTotal - woCancelled);
  const manualWorkPct = Number(mon?.summary?.physical_progress_pct || 0);
  const progressPct = isMaintenanceProject && woEff > 0 ? (woDone / woEff) * 100 : manualWorkPct;
  const workProgressText = isWoProject
    ? `Progress Pekerjaan: ${proj.status === "completed" ? "Sudah Selesai" : "Belum Selesai"}`
    : isMaintenanceProject
      ? `Progress Pekerjaan ${progressPct.toFixed(0)}% · ${woDone}/${woEff} WO selesai`
      : `Progress Pekerjaan ${progressPct.toFixed(0)}%`;
  const showWorkProgress = isWoProject || (isMaintenanceProject ? woEff > 0 : progressPct > 0);
  const canEdit = canPerm(user, "projects", proj.status === "completed" ? "delete" : "update");
  // 2026-06-19 (user request): tombol Hapus di header monitoring (gated projects.delete);
  // tombol Edit di header DIHILANGKAN — edit dilakukan dari tab Data Proyek.
  const canDelete = canPerm(user, "projects", "delete");
  // 2026-06-20 (user request): tombol Tutup Project di header monitoring.
  // Backend PATCH /active-projects/{pid}/close (perm projects.close). End date wajib —
  // kalau kosong backend 400, user isi dulu lewat tab Data Proyek.
  const canClose = proj.status !== "completed" && proj.project_type !== "internal" && canPerm(user, "projects", "close");
  const doClose = async () => {
    setCloseBusy(true);
    try {
      await api.patch(`/active-projects/${pid}/close`, {});
      toast.success(`Project "${proj.project_name}" ditutup.`);
      setConfirmClose(false);
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menutup project.");
    } finally {
      setCloseBusy(false);
    }
  };
  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/active-projects/${pid}`);
      toast.success(`Project "${proj.project_name}" dihapus.`);
      nav("/projects");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menghapus project.");
      setDelBusy(false);
    }
  };
  const pic = idf.pic_name || proj.created_by_name || "—";
  const contractPo = idf.contract_number || proj.wo_number || "—";

  return (
    <div className="p-6 space-y-4" data-testid="monitoring-project-page">
      {/* Breadcrumb */}
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
        <Link to="/projects" className="hover:text-[#0B1D3A]">Proyek</Link>
        <span className="text-slate-400">›</span>
        <Link to="/projects" className="hover:text-[#0B1D3A]">Manajemen Proyek</Link>
        <span className="text-slate-400">›</span>
        <span className="text-slate-700">Monitoring</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight" data-testid="mp-title">
              Dashboard Monitoring – {proj.project_name}
            </h1>
            <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${status.cls || "bg-emerald-50 border-emerald-300 text-emerald-700"}`}
              data-testid="mp-status-pill">{status.label || "Aktif"}</span>
            {/* 2026-06-20 (user request): badge Status OPERASIONAL — fisik 100%
                TIDAK otomatis Closed; tooltip = alasan status. */}
            <OpStatusBadge project={proj} override={mon?.summary?.operational_status} testid="mp-op-status-pill" className="!text-[10px] !px-2" />
            {showWorkProgress && (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-sky-300 bg-sky-50 text-sky-700 tabular-nums"
                data-testid="mp-progress-pill">{workProgressText}</span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-[#0B1D3A]" data-testid="mp-client">{idf.client || "—"}</span>
            <span className="text-slate-400">•</span>
            <span>{contractPo}</span>
            <span className="text-slate-400">•</span>
            <span>PIC {pic}</span>
            {/* 2026-06-21 (user request): Nilai Kontrak = JUMLAHAN induk + VO
                (umum) / Σ WO (maintenance). Rincian terbaca, tanpa peringatan. */}
            {(() => {
              const sub = Number(mon?.summary?.sub_value_sum || 0);
              const total = sub > 0 ? contractVal + sub : contractVal;
              if (total <= 0) return null;
              return (<><span className="text-slate-400">•</span><span className="tabular-nums" data-testid="mp-contract-total">Nilai Kontrak <b className="text-[#0B1D3A]">{formatIDR(total)}</b>{sub > 0 && <span className="text-slate-400 font-normal"> ({contractVal > 0 ? `induk ${formatIDR(contractVal)} + ` : ""}{proj.is_outlet ? "WO" : "VO"} {formatIDR(sub)})</span>}</span></>);
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* 2026-06-20 (user request): dari monitoring WO/VO (child), tombol kembali
              mengarah ke Dashboard Monitoring project induk (tab Progress Lapangan). */}
          <button type="button" data-testid="mp-btn-back" onClick={() => nav(proj.parent_project_id ? `/projects/${proj.parent_project_id}/monitoring?tab=progress-lapangan` : "/projects")}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <ArrowLeft size={13} /> {proj.parent_project_id ? "Kembali ke Project Induk" : "Kembali ke Project"}
          </button>
          {canClose && (
            <button type="button" data-testid="mp-btn-close" onClick={() => setConfirmClose(true)} disabled={closeBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
              <CheckCircle2 size={13} /> {closeBusy ? "Menutup…" : "Tutup Project"}
            </button>
          )}
          {canDelete && (
            <button type="button" data-testid="mp-btn-delete" onClick={() => setConfirmDel(true)} disabled={delBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
              <Trash2 size={13} /> {delBusy ? "Menghapus…" : "Hapus Project"}
            </button>
          )}
        </div>
      </div>

      {/* 2026-06-20 (user request): peringatan konflik identitas anak vs induk. */}
      {identity.conflicts.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-start gap-2" data-testid="mp-identity-conflict">
          <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <span className="font-semibold">Konflik data dengan proyek induk{identity.source?.project_name ? ` (${identity.source.project_name})` : ""}: </span>
            {identity.conflicts.map((c) => `${c.label} sub ("${c.childValue}") ≠ induk ("${c.parentValue}")`).join(" · ")}.
            Nilai yang ditampilkan mengikuti data induk — periksa & selaraskan di tab Data Proyek bila perlu.
          </div>
        </div>
      )}

      {/* 2026-06-20 (user request): widget Profit Cost Control — terhubung ke
          tabel Cost Control proyek (item pekerjaan + overhead cost). */}
      {ccSum && (
        <div className="border border-slate-200 bg-white px-4 py-3 flex items-center gap-6 flex-wrap" data-testid="mp-cost-control-widget">
          <div>
            <div className="overline text-slate-500">Cost Control</div>
            <button type="button" data-testid="mp-cc-open" onClick={() => nav(`/projects/${pid}/cost-control`)}
              className="mt-1 text-[11px] font-semibold text-[#0B1D3A] hover:text-[#FF5A1F] underline underline-offset-2">
              {ccSum.item_count > 0 ? `${ccSum.item_count} item · Buka Tabel` : "Belum ada item · Isi Tabel"}
            </button>
          </div>
          <div data-testid="mp-cc-kontrak">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Harga Kontrak</div>
            <div className="text-sm font-bold tabular-nums text-[#0B1D3A]">{formatIDR(ccSum.total_kontrak)}</div>
          </div>
          <div data-testid="mp-cc-expense">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Expense</div>
            <div className="text-sm font-bold tabular-nums text-rose-700">{formatIDR(ccSum.total_expense)}</div>
          </div>
          <div data-testid="mp-cc-profit">
            {/* R227: label & angka dari profit_view kanonis — "Selisih
                Sementara" sampai biaya dikonfirmasi lengkap.
                R229: status unknown → TANPA angka selisih bertanda. */}
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{ccSum.profit_view?.labels?.selisih || "Selisih Sementara"}</div>
            <button type="button" data-testid="mp-cc-profit-btn" onClick={() => setCcDrillOpen(true)}
              className={`text-sm font-bold tabular-nums hover:underline underline-offset-2 ${ccSum.profit_view?.completeness?.status === "complete" ? (ccSum.profit_view.selisih >= 0 ? "text-emerald-700" : "text-rose-700") : ccSum.profit_view?.completeness?.status === "incomplete" ? "text-amber-700" : "text-slate-500 italic font-semibold"}`}
              title="Klik untuk rincian pembentuk angka">
              {ccSum.profit_view?.completeness?.status === "unknown" ? "Belum Dapat Dipastikan" : formatIDR(ccSum.profit_view?.selisih ?? ccSum.profit)}
            </button>
          </div>
          <div data-testid="mp-cc-margin">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{ccSum.profit_view?.labels?.margin || "Margin Sementara"}</div>
            <div className="text-sm font-bold tabular-nums text-slate-700">
              {(ccSum.profit_view?.margin ?? null) === null || ccSum.profit_view?.completeness?.status === "unknown" ? <span className="text-[10px] font-normal text-slate-500">Margin belum dapat dihitung</span> : `${ccSum.profit_view.margin.toFixed(1)}%`}
            </div>
          </div>
          {ccSum.profit_view && (
            <ProfitStatusBadge view={ccSum.profit_view} onClick={() => setCcDrillOpen(true)} testid="mp-cc-badge" />
          )}
          <ProfitDrilldownDialog open={ccDrillOpen} onOpenChange={setCcDrillOpen} view={ccSum.profit_view} projectName={proj?.project_name} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 flex flex-wrap gap-1" data-testid="mp-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" data-testid={`mp-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px transition-colors ${activeTab === t.key ? "border-[#0B1D3A] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}>
            {/* 2026-06-19 (user request): project non-maintenance (is_outlet=false)
                menampilkan "Variation Order"; parent maintenance (is_outlet=true)
                tetap "Working Order". */}
            {t.key === "working-order" && !proj?.is_outlet ? "Variation Order" : t.label}
          </button>
        ))}
      </div>

      {/* Tab content — Fase 3: semua 7 tab terisi. */}
      <div data-testid={`mp-panel-${activeTab}`} className="min-h-[240px]">
        {activeTab === "ringkasan" && <RingkasanTab project={proj} mon={mon} ccSum={ccSum} identity={identity} onDataChanged={load} />}
        {activeTab === "progress-lapangan" && <ProgressLapanganTab project={proj} mon={mon} canEdit={canEdit} onChanged={load} />}
        {activeTab === "keuangan" && <KeuanganTab mon={mon} canViewFinance={canPerm(user, "finance_report", "view")} />}
        {activeTab === "cash-flow" && <CashFlowTab mon={mon} canViewFinance={canPerm(user, "finance_report", "view")} />}
        {activeTab === "working-order" && <WorkingOrderTab mon={mon} project={proj} />}
        {activeTab === "data-proyek" && <DataProyekTab project={proj} mon={mon} canEdit={canEdit} onEdit={() => setEditOpen(true)} onSaved={load} identity={identity} />}
        {activeTab === "riwayat-aktivitas" && <RiwayatAktivitasTab mon={mon} project={proj} />}
      </div>

      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={proj}
        onSaved={() => { setEditOpen(false); load(); }}
      />

      <ConfirmDialog
        open={confirmDel}
        onOpenChange={setConfirmDel}
        title={`Hapus project "${proj.project_name}"?`}
        description="Project beserta sub VO/WO dan ledger keuangannya akan dihapus permanen. Tindakan ini tidak bisa dibatalkan."
        confirmLabel={delBusy ? "Menghapus…" : "Hapus Permanen"}
        onConfirm={() => { setConfirmDel(false); doDelete(); }}
        testid="mp-confirm-delete"
      />

      {/* 2026-06-20 (user request): konfirmasi Tutup Project dari header monitoring. */}
      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title={`Tutup project "${proj.project_name}"?`}
        description="Project akan berstatus Selesai dan total keuangannya di-snapshot. Tanggal selesai (end date) wajib sudah terisi — bila belum, isi dulu lewat tab Data Proyek."
        confirmLabel={closeBusy ? "Menutup…" : "Tutup Project"}
        onConfirm={() => { doClose(); }}
        testid="mp-confirm-close"
      />
    </div>
  );
}
